import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, InfoNote, Spinner, ValidatedInput } from '../../components'
import CountryPickerModal from '../CountryPickerModal'
import countries from '../../countries.json'
import { isEmailValid, isTelephoneValid, isVerificationCodeValid } from '../../helpers'
import { appsUsernameSelected, verificationCodeSentKey, defaultPhoneCountryCode } from '../../helpers/app'
import { ICountry } from '../../models/country'
import { LiteralsService } from '../../services'
import {
  confirmVerificationCodeAction,
  setVerificationCallingCodeAction,
  setVerificationCodeAction,
  setVerificationCountryAction,
  setVerificationEmailAction,
  setVerificationPhoneAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'

const isPhoneVerification = appsUsernameSelected?.username === 'phone'

interface IOwnProps {
  // "Ask for a new code": the host decides whether that is a page or another screen of a modal.
  onResend: () => void
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  language: string
  email: string
  phone: string
  code: string
  isVerificating: boolean
  country?: ICountry
}

/** Entering the account verification code, shared by the page and the guest prompt. */
class VerificationStepForm extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneVerification && !props.country) {
      const defaultCountry = countries.find(country => country.code === defaultPhoneCountryCode)
      if (defaultCountry) {
        props.dispatch(setVerificationCountryAction(defaultCountry))
        props.dispatch(setVerificationCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { email, phone, code, isVerificating } = this.props

    if (isPhoneVerification) return !isTelephoneValid(phone) || !isVerificationCodeValid(code) || isVerificating
    return !isEmailValid(email) || !isVerificationCodeValid(code) || isVerificating
  }

  private get verificationButton(): ReactNode {
    if (this.props.isVerificating) return <div className="loading"><Spinner /></div>
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.handleValidatedButtonPress }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {
    const { email, phone, code, country, onResend } = this.props

    return (
      <>
        <section>
          {
            isPhoneVerification ? (
              <ValidatedInput
                name="phone"
                phone
                label={ LiteralsService.get('phone', true) }
                value={ phone }
                country={ country }
                onChange={ this.onPhoneChange }
                error={ !isTelephoneValid(phone) }
              />
            ) : (
              <ValidatedInput
                name="email"
                label={ LiteralsService.get('email', true) }
                value={ email }
                onChange={ this.onEmailChange }
                error={ !isEmailValid(email) }
              />
            )
          }
          <ValidatedInput
            name="code"
            label={ LiteralsService.get('verificationCode', true) }
            value={ code }
            onChange={ this.onCodeChange }
            error={ !isVerificationCodeValid(code) }
          />
          <InfoNote literal={ verificationCodeSentKey } />
        </section>
        { isPhoneVerification && <CountryPickerModal selectCountry={ this.onCountryChange } selectedCountry={ this.props.country } /> }
        <section className="actions">
          { this.verificationButton }
          <div className="link">
            <span onClick={ onResend }>
              <strong>{ LiteralsService.get('requestNewSignUpVerificationCode', true) }.</strong>
            </span>
          </div>
        </section>
      </>
    )
  }

  private onEmailChange = (email: string) => {
    this.props.dispatch(setVerificationEmailAction(email))
  }

  private onPhoneChange = (phone: string) => {
    this.props.dispatch(setVerificationPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setVerificationCountryAction(country))
    dispatch(setVerificationCallingCodeAction(country.dial_code))
  }

  private onCodeChange = (code: string) => {
    this.props.dispatch(setVerificationCodeAction(code))
  }

  private handleValidatedButtonPress = () => {
    this.props.dispatch(confirmVerificationCodeAction())
  }
}

const mapStateToProps = ({ signUp, verification, configuration }: IStoreState) => ({
  // Redraws the literals when the language selector changes the language.
  language: configuration.language,
  email: verification.email ? verification.email : signUp.email,
  phone: verification.phone ? verification.phone : signUp.phone,
  code: verification.code,
  isVerificating: verification.isVerificating,
  country: verification.country
})

export default connectRedux(mapStateToProps, VerificationStepForm) as React.ComponentType<IOwnProps>
